import { InquiryStageEnum, QuoteStatusEnum } from '@/gen/cortado/v1/settings_pb'
import { isBooked } from './enum-utils'

/**
 * Stages where a lead is still being worked (shown by the "Active" stage scope
 * on the leads page).
 */
export const ACTIVE_LEAD_STAGES: InquiryStageEnum[] = [
  InquiryStageEnum.INQUIRY_STAGE_NEW,
  InquiryStageEnum.INQUIRY_STAGE_IN_PROGRESS,
  InquiryStageEnum.INQUIRY_STAGE_QUOTE_SENT,
]

/**
 * Terminal stages — the lead has been won, lost, gone cold, or referred to
 * another business. Hidden by the "Active" stage scope; shown by "All".
 */
export const CLOSED_LEAD_STAGES: InquiryStageEnum[] = [
  InquiryStageEnum.INQUIRY_STAGE_ACCEPTED,
  InquiryStageEnum.INQUIRY_STAGE_REJECTED,
  InquiryStageEnum.INQUIRY_STAGE_STALE,
  InquiryStageEnum.INQUIRY_STAGE_REFERRED,
]

/**
 * Stages a person can move a lead into. Referred is set only when another
 * business accepts a referral of the lead, so it's shown but never offered.
 */
export function isSelectableLeadStage(stage: InquiryStageEnum): boolean {
  return stage !== InquiryStageEnum.INQUIRY_STAGE_UNKNOWN && stage !== InquiryStageEnum.INQUIRY_STAGE_REFERRED
}

/**
 * What moving a lead into a stage does, from a stage menu or the board:
 * - `refuse-referred`: Referred is set only by an accepted referral.
 * - `reject`: ask for a reason first; the reason dialog saves the stage.
 * - `book`: Accepted is set only by booking the lead (the server refuses a
 *   hand-made move to it), so picking it opens booking instead.
 * - `refuse-booked`: an already-booked lead's stage belongs to its booking.
 * - `update`: save the stage.
 */
export type LeadStageMove = 'refuse-referred' | 'reject' | 'book' | 'refuse-booked' | 'update'

export function leadStageMove(stage: InquiryStageEnum, lead: { bookingId?: string }): LeadStageMove {
  if (stage === InquiryStageEnum.INQUIRY_STAGE_REFERRED) return 'refuse-referred'
  if (isBooked(lead)) return 'refuse-booked'
  if (stage === InquiryStageEnum.INQUIRY_STAGE_REJECTED) return 'reject'
  if (stage === InquiryStageEnum.INQUIRY_STAGE_ACCEPTED) return 'book'
  return 'update'
}

export function isActiveLeadStage(stage: InquiryStageEnum): boolean {
  return ACTIVE_LEAD_STAGES.includes(stage)
}

/**
 * Returns true when an inquiry has been marked ACCEPTED but no booking/event
 * has been generated from it yet. Used to surface "no event yet" affordances
 * on the leads list, kanban, and detail page.
 *
 * Accepts any object with `stage` + `bookingId`, so it works against Lead,
 * InquiryLite, and Inquiry proto shapes without importing proto types here.
 */
export function isAcceptedWithoutEvent(
  inquiry: { stage: InquiryStageEnum; bookingId?: string }
): boolean {
  return (
    inquiry.stage === InquiryStageEnum.INQUIRY_STAGE_ACCEPTED &&
    !isBooked(inquiry)
  )
}

/**
 * True when a quote can never be accepted because its lead is already
 * booked and this isn't the quote that produced the booking. An inquiry
 * only carries a single `bookingId`, so any quote other than the ACCEPTED
 * one is a dead sibling once a booking exists — mirrors the customer-facing
 * rule in portal's `isSiblingQuoteBooking` (see portal/src/components/quote-detail.tsx).
 */
export function isDeadQuoteOnBookedLead(
  inquiry: { bookingId?: string },
  quoteStatus: QuoteStatusEnum
): boolean {
  return isBooked(inquiry) && quoteStatus !== QuoteStatusEnum.QUOTE_STATUS_ACCEPTED
}
